import { useCallback, useEffect, useState } from 'react';
import { RecipesApi, SalesApi, UsersApi } from '../api/resources';
import { ApiError } from '../api/client';
import type { Recipe, Sale, User } from '../api/types';
import { salePaymentMethodLabels } from '../api/types';
import { useToast } from '../context/ToastContext';
import { formatCOP, formatDateTime, formatQty } from '../utils/format';

type PeriodMode = 'month' | 'day' | 'range';

function toInputDate(date: Date) {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

function startOfDay(value: string) {
  return new Date(`${value}T00:00:00`).toISOString();
}

function endOfDay(value: string) {
  return new Date(`${value}T23:59:59.999`).toISOString();
}

export function SalesHistory() {
  const { notify } = useToast();
  const [users, setUsers] = useState<User[]>([]);
  const [recipes, setRecipes] = useState<Recipe[]>([]);
  const [userId, setUserId] = useState('');
  const [recipeId, setRecipeId] = useState('');
  const [mode, setMode] = useState<PeriodMode>('month');
  const [day, setDay] = useState(toInputDate(new Date()));
  const [from, setFrom] = useState(toInputDate(new Date()));
  const [to, setTo] = useState(toInputDate(new Date()));
  const [sales, setSales] = useState<Sale[]>([]);
  const [total, setTotal] = useState(0);
  const [totalAmount, setTotalAmount] = useState('0');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    UsersApi.list().then((r) => setUsers(r.users)).catch(() => undefined);
    RecipesApi.list({}).then((r) => setRecipes(r.recipes)).catch(() => undefined);
  }, []);

  const load = useCallback(async () => {
    let fromIso: string | undefined;
    let toIso: string | undefined;
    if (mode === 'month') {
      const now = new Date();
      fromIso = new Date(now.getFullYear(), now.getMonth(), 1).toISOString();
      toIso = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999).toISOString();
    } else if (mode === 'day') {
      if (!day) return;
      fromIso = startOfDay(day);
      toIso = endOfDay(day);
    } else {
      if (!from || !to) return;
      if (from > to) {
        notify('La fecha inicial no puede ser posterior a la final.', 'error');
        return;
      }
      fromIso = startOfDay(from);
      toIso = endOfDay(to);
    }

    setLoading(true);
    try {
      const result = await SalesApi.list({
        userId: userId || undefined,
        recipeId: recipeId || undefined,
        from: fromIso,
        to: toIso,
        page,
        pageSize: 20,
      });
      setSales(result.items);
      setTotal(result.total);
      setTotalPages(result.totalPages);
      setTotalAmount(String(result.totalAmount));
    } catch (err) {
      notify(err instanceof ApiError ? err.message : 'No fue posible cargar las ventas.', 'error');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, day, from, to, userId, recipeId, page]);

  useEffect(() => {
    void load();
  }, [load]);

  function changeFilter<T>(setter: (value: T) => void) {
    return (value: T) => {
      setPage(1);
      setter(value);
    };
  }

  return (
    <div>
      <h1>Historia de ventas</h1>
      <p className="text-muted">Ventas de todos los empleados, de la más reciente a la más antigua. Por defecto se muestra el mes actual.</p>

      <div className="card">
        <div className="form-grid">
          <div className="field">
            <label htmlFor="hsEmployee">Empleado</label>
            <select id="hsEmployee" value={userId} onChange={(e) => changeFilter(setUserId)(e.target.value)}>
              <option value="">Todos los empleados</option>
              {users.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.fullName}
                </option>
              ))}
            </select>
          </div>
          <div className="field">
            <label htmlFor="hsProduct">Producto</label>
            <select id="hsProduct" value={recipeId} onChange={(e) => changeFilter(setRecipeId)(e.target.value)}>
              <option value="">Todos los productos</option>
              {recipes.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.code} - {r.name}
                </option>
              ))}
            </select>
          </div>
          <div className="field">
            <label htmlFor="hsMode">Periodo</label>
            <select id="hsMode" value={mode} onChange={(e) => changeFilter(setMode)(e.target.value as PeriodMode)}>
              <option value="month">Mes actual</option>
              <option value="day">Fecha específica</option>
              <option value="range">Rango de fechas</option>
            </select>
          </div>
          {mode === 'day' && (
            <div className="field">
              <label htmlFor="hsDay">Fecha</label>
              <input id="hsDay" type="date" value={day} onChange={(e) => changeFilter(setDay)(e.target.value)} />
            </div>
          )}
          {mode === 'range' && (
            <>
              <div className="field">
                <label htmlFor="hsFrom">Desde</label>
                <input id="hsFrom" type="date" value={from} onChange={(e) => changeFilter(setFrom)(e.target.value)} />
              </div>
              <div className="field">
                <label htmlFor="hsTo">Hasta</label>
                <input id="hsTo" type="date" value={to} onChange={(e) => changeFilter(setTo)(e.target.value)} />
              </div>
            </>
          )}
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <h2>Totalización</h2>
        </div>
        <p>
          <strong>{total}</strong> {total === 1 ? 'venta' : 'ventas'} &middot; Total: <strong>{formatCOP(totalAmount)}</strong>
        </p>
      </div>

      <div className="card">
        {loading ? (
          <div className="loading-block">Cargando ventas...</div>
        ) : sales.length === 0 ? (
          <p className="empty-state">No hay ventas para los filtros seleccionados.</p>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Fecha</th>
                  <th>Empleado</th>
                  <th>Documento</th>
                  <th>Cliente</th>
                  <th>Productos</th>
                  <th>Medio de pago</th>
                  <th className="text-right">Total</th>
                </tr>
              </thead>
              <tbody>
                {sales.map((sale) => (
                  <tr key={sale.id}>
                    <td>{formatDateTime(sale.createdAt)}</td>
                    <td>{sale.user?.fullName ?? '—'}</td>
                    <td>{sale.clientDocument}</td>
                    <td>{sale.clientName}</td>
                    <td>
                      {sale.items.map((item) => (
                        <div key={item.id}>
                          {item.recipeNameSnapshot} &times; {formatQty(item.quantity)}
                        </div>
                      ))}
                    </td>
                    <td>{salePaymentMethodLabels[sale.paymentMethod] ?? sale.paymentMethod}</td>
                    <td className="text-right">{formatCOP(sale.totalCost)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {totalPages > 1 && (
          <div className="actions-row mt-1">
            <button type="button" className="btn btn-secondary" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
              Anterior
            </button>
            <span>
              Página {page} de {totalPages}
            </span>
            <button type="button" className="btn btn-secondary" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>
              Siguiente
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
